import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/cn';
import { MAX_RULE_DEPTH, MAX_RULE_NODES, ruleNodeSchema } from '@personalizehq/schemas';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';

// The editor's tree is LOOSER than `RuleNode`: a half-typed number ("1" → "1." → "1.5") and an
// `in` list with no items yet are states the author passes through, and the strict union has no
// room for them. `ruleNodeSchema` is the gate at save (validateRule below), not at keystroke.
export type RuleDraft =
  | { kind: 'trait'; trait: string; op: TraitOp; value: unknown }
  | { kind: 'event'; event: string; op: ComparisonOp; count: unknown; window_days: unknown }
  | { kind: 'and' | 'or'; rules: RuleDraft[] };

type ComparisonOp = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte';
type TraitOp = ComparisonOp | 'in' | 'not_in';

const COMPARISON_OPS: ComparisonOp[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'];
const TRAIT_OPS: TraitOp[] = [...COMPARISON_OPS, 'in', 'not_in'];
const OP_LABELS: Record<TraitOp, string> = {
  eq: 'is',
  neq: 'is not',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  in: 'is one of',
  not_in: 'is none of',
};

const MAX_WINDOW_DAYS = 90;

const isGroup = (n: RuleDraft): n is Extract<RuleDraft, { rules: RuleDraft[] }> => 'rules' in n;
const isMembership = (op: TraitOp): boolean => op === 'in' || op === 'not_in';

export const newTrait = (): RuleDraft => ({ kind: 'trait', trait: '', op: 'eq', value: '' });
export const newEvent = (): RuleDraft => ({
  kind: 'event',
  event: '',
  op: 'gte',
  count: 1,
  window_days: 7,
});
/** What a new segment is created with. It must already pass `segmentRuleSchema` — the API 400s an
 *  invalid definition on POST, so a blank row is not an option here. */
export const defaultRule = (): RuleDraft => ({
  kind: 'and',
  rules: [{ kind: 'trait', trait: 'plan', op: 'eq', value: 'pro' }],
});

/** Mirrors `checkRawBounds` in packages/schemas/src/rules.ts — root is depth 1, every node counts. */
export function ruleStats(node: RuleDraft): { nodes: number; depth: number } {
  let nodes = 0;
  let depth = 0;
  const stack: Array<{ node: RuleDraft; depth: number }> = [{ node, depth: 1 }];
  while (stack.length > 0) {
    const top = stack.pop() as { node: RuleDraft; depth: number };
    nodes++;
    if (top.depth > depth) depth = top.depth;
    if (isGroup(top.node)) {
      for (const child of top.node.rules) stack.push({ node: child, depth: top.depth + 1 });
    }
  }
  return { nodes, depth };
}

/** The zod message, verbatim — the schema is the law, this component only surfaces it. */
export function validateRule(node: RuleDraft): string | null {
  const { nodes, depth } = ruleStats(node);
  if (depth > MAX_RULE_DEPTH) return `Rule tree is deeper than ${MAX_RULE_DEPTH} levels.`;
  if (nodes > MAX_RULE_NODES) return `Rule tree has more than ${MAX_RULE_NODES} nodes.`;
  const parsed = ruleNodeSchema.safeParse(node);
  if (parsed.success) return null;
  const issue = parsed.error.issues[0];
  return issue ? `${issue.path.join('.') || 'rule'}: ${issue.message}` : 'Invalid rule.';
}

// A trait value round-trips through one text box, so the text has to carry its own type. Numbers
// and booleans win over strings; the trait "42" as a *string* is therefore not expressible here.
// ponytail: a per-row type picker is the upgrade path, when someone actually stores "42".
function coerceScalar(raw: string): string | number | boolean {
  const text = raw.trim();
  if (text === '') return '';
  if (text === 'true') return true;
  if (text === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(text)) return Number(text);
  return text;
}

function displayValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ');
  if (value === null || value === undefined) return '';
  return String(value);
}

function parseValue(raw: string, op: TraitOp): unknown {
  if (!isMembership(op)) return coerceScalar(raw);
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .map(coerceScalar);
}

// ---------------------------------------------------------------------------- tree edits

/** Immutable replace at an index path; `next === null` removes the node. */
function editAt(node: RuleDraft, path: number[], next: RuleDraft | null): RuleDraft {
  const [head, ...rest] = path;
  if (head === undefined) return next ?? node;
  if (!isGroup(node)) return node;
  const child = node.rules[head];
  if (child === undefined) return node;

  const replacement = rest.length > 0 ? editAt(child, rest, next) : next;
  const rules =
    replacement === null
      ? node.rules.filter((_, i) => i !== head)
      : node.rules.map((r, i) => (i === head ? replacement : r));
  return { ...node, rules };
}

// ---------------------------------------------------------------------------- component

export function RuleBuilder({
  value,
  onChange,
}: {
  value: RuleDraft;
  onChange: (next: RuleDraft) => void;
}): React.JSX.Element {
  const { nodes, depth } = ruleStats(value);
  const error = validateRule(value);

  const set = (path: number[], next: RuleDraft | null): void => {
    // The root group is never removable — a segment with no rule is not a segment.
    if (path.length === 0 && next === null) return;
    onChange(editAt(value, path, next));
  };

  return (
    <div className="space-y-3">
      <GroupNode node={value} path={[]} set={set} />
      <BoundsMeter nodes={nodes} depth={depth} />
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

function BoundsMeter({ nodes, depth }: { nodes: number; depth: number }): React.JSX.Element {
  // Amber at 80% of either bound: the spec's "surfaced as a counter, not a surprise" (§2).
  const near = nodes >= MAX_RULE_NODES * 0.8 || depth >= MAX_RULE_DEPTH * 0.8;
  const over = nodes > MAX_RULE_NODES || depth > MAX_RULE_DEPTH;
  return (
    <p
      data-testid="bounds-meter"
      className={cn(
        'text-xs tabular-nums',
        over ? 'text-destructive' : near ? 'text-amber-600' : 'text-muted-foreground'
      )}
    >
      {nodes} / {MAX_RULE_NODES} nodes · depth {depth} / {MAX_RULE_DEPTH}
    </p>
  );
}

type Setter = (path: number[], next: RuleDraft | null) => void;

function GroupNode({
  node,
  path,
  set,
}: {
  node: RuleDraft;
  path: number[];
  set: Setter;
}): React.JSX.Element {
  if (!isGroup(node)) return <PredicateRow node={node} path={path} set={set} />;

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex items-center gap-2">
        <Select
          value={node.kind}
          onValueChange={(kind) => set(path, { ...node, kind: kind as 'and' | 'or' })}
        >
          <SelectTrigger className="h-8 w-[110px]" aria-label="Group operator">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="and">Match ALL</SelectItem>
            <SelectItem value="or">Match ANY</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">of the following</span>
        {path.length > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto size-8"
            aria-label="Remove group"
            onClick={() => set(path, null)}
          >
            <X className="size-4" />
          </Button>
        )}
      </div>

      {node.rules.map((child, i) => (
        <GroupNode key={[...path, i].join('.')} node={child} path={[...path, i]} set={set} />
      ))}

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => set(path, { ...node, rules: [...node.rules, newTrait()] })}
        >
          <Plus className="mr-1 size-3.5" /> Condition
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            set(path, { ...node, rules: [...node.rules, { kind: 'and', rules: [newTrait()] }] })
          }
        >
          <Plus className="mr-1 size-3.5" /> Group
        </Button>
      </div>
    </div>
  );
}

/** The text box owns its own text; the AST is derived from it, never the other way round. Deriving
 *  the text back from the AST on every keystroke eats the space in "New York" (trim) and the comma
 *  in "gold, platinum" (the empty tail is dropped) — you could not type either one. */
function ValueInput({
  op,
  value,
  onChange,
}: {
  op: TraitOp;
  value: unknown;
  onChange: (value: unknown) => void;
}): React.JSX.Element {
  const [text, setText] = useState(() => displayValue(value));
  return (
    <Input
      className="h-8 w-[200px]"
      aria-label="Value"
      placeholder={isMembership(op) ? 'pro, enterprise' : 'pro'}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(parseValue(e.target.value, op));
      }}
    />
  );
}

function PredicateRow({
  node,
  path,
  set,
}: {
  node: Exclude<RuleDraft, { rules: RuleDraft[] }>;
  path: number[];
  set: Setter;
}): React.JSX.Element {
  const ops: TraitOp[] = node.kind === 'trait' ? TRAIT_OPS : COMPARISON_OPS;

  const changeOp = (op: TraitOp): void => {
    if (node.kind === 'event') {
      set(path, { ...node, op: op as ComparisonOp });
      return;
    }
    // Crossing the membership boundary changes the value's SHAPE (scalar ↔ list), so it converts
    // rather than carries: `in` with a scalar value would only ever be a zod error.
    const was = isMembership(node.op);
    const now = isMembership(op);
    const value =
      was === now
        ? node.value
        : now
          ? parseValue(displayValue(node.value), op)
          : coerceScalar(displayValue(node.value));
    set(path, { ...node, op, value });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/40 p-2">
      <Select
        value={node.kind}
        onValueChange={(kind) => set(path, kind === 'trait' ? newTrait() : newEvent())}
      >
        <SelectTrigger className="h-8 w-[100px]" aria-label="Predicate kind">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="trait">Trait</SelectItem>
          <SelectItem value="event">Event</SelectItem>
        </SelectContent>
      </Select>

      {node.kind === 'trait' ? (
        <Input
          className="h-8 w-[160px]"
          aria-label="Trait"
          placeholder="plan"
          value={node.trait}
          onChange={(e) => set(path, { ...node, trait: e.target.value })}
        />
      ) : (
        <Input
          className="h-8 w-[180px]"
          aria-label="Event name"
          placeholder="deposit_completed"
          value={node.event}
          onChange={(e) => set(path, { ...node, event: e.target.value })}
        />
      )}

      <Select value={node.op} onValueChange={(op) => changeOp(op as TraitOp)}>
        <SelectTrigger className="h-8 w-[130px]" aria-label="Operator">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ops.map((op) => (
            <SelectItem key={op} value={op}>
              {OP_LABELS[op]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {node.kind === 'trait' ? (
        <ValueInput
          op={node.op}
          value={node.value}
          onChange={(value) => set(path, { ...node, value })}
        />
      ) : (
        <>
          <Input
            className="h-8 w-[80px]"
            type="number"
            aria-label="Count"
            value={String(node.count ?? '')}
            onChange={(e) => set(path, { ...node, count: coerceScalar(e.target.value) })}
          />
          <span className="text-xs text-muted-foreground">times in the last</span>
          <Input
            className="h-8 w-[80px]"
            type="number"
            min={1}
            max={MAX_WINDOW_DAYS}
            aria-label="Window days"
            value={String(node.window_days ?? '')}
            onChange={(e) => set(path, { ...node, window_days: coerceScalar(e.target.value) })}
          />
          <span className="text-xs text-muted-foreground">days (max {MAX_WINDOW_DAYS})</span>
        </>
      )}

      <Button
        variant="ghost"
        size="icon"
        className="ml-auto size-8"
        aria-label="Remove condition"
        onClick={() => set(path, null)}
      >
        <X className="size-4" />
      </Button>
    </div>
  );
}
